import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { useDateFormatter } from './useDateFormatter';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_CreateRefundInput.ts';
import '../contracts/Payroll_UpdateRefundInput.ts';
import '../contracts/Payroll_AcceptRefundInput.ts';
import '../contracts/Payroll_DeclineRefundInput.ts';
import '../contracts/Payroll_ProcessRefundsBatchInput.ts';
import '../contracts/Payroll_AcceptRefundResponse';
import '../contracts/Payroll_CreateRefundResponse';
import '../contracts/Payroll_DeclineRefundResponse';
import '../contracts/Payroll_DeleteRefundResponse';
import '../contracts/Payroll_PendingRefundsResponse';
import '../contracts/Payroll_ProcessRefundsBatchResponse';
import '../contracts/Payroll_RefundResponse';
import '../contracts/Payroll_RefundsResponse';
import '../contracts/Payroll_RefundsSummaryResponse';
import '../contracts/Payroll_UpdateRefundResponse';
import '../contracts/Payroll_UploadRefundReceiptResponse';

import type { Payroll_AcceptRefundInput } from '../contracts/Payroll_AcceptRefundInput';
import type { Payroll_CreateRefundInput } from '../contracts/Payroll_CreateRefundInput';
import type { Payroll_DeclineRefundInput } from '../contracts/Payroll_DeclineRefundInput';
import type { Payroll_UpdateRefundInput } from '../contracts/Payroll_UpdateRefundInput';
import type { Payroll_ProcessRefundsBatchInput } from '../contracts/Payroll_ProcessRefundsBatchInput';
/**
 * Composable for managing payroll refund requests
 *
 * Payroll refunds track expenses paid by operadores with their own money
 * that need to be reimbursed. Operadores can submit refund requests with
 * receipt attachments, which managers can accept or decline.
 *
 * Workflow:
 * 1. Operador creates refund request with receipt
 * 2. Manager accepts/declines from Refunds page or Payroll Wizard
 * 3. On accept: creates a despesa with status 'pendente'
 * 4. Refund is paid when the despesa is 'baixada'
 *
 * Status values:
 * - -1: DECLINED
 * -  0: PENDING
 * -  1: ACCEPTED
 *
 * TYPED DATA LAYER: Date handling follows the typed data layer pattern:
 * - OUTGOING datetime: Date objects → ISO 8601 via toApiDateTimeFormat
 * - INCOMING datetime: ISO 8601 strings → Date objects via fromApiDateTimeFormat
 * - OUTGOING date-only: Date objects → "YYYY-MM-DD" via fromDateToApiDate (no UTC conversion)
 * - INCOMING date-only: "YYYY-MM-DD" strings → Date objects via fromApiDateToDate (noon local)
 * Conversions happen at this composable boundary, not in components.
 */
export function usePayrollRefunds() {
  const isLoading = ref(false);
  const error = ref(null);

  // Initialize date formatter for typed data layer conversions
  const {
    fromApiDateToDate,
    fromApiDateTimeFormat,
    fromDateToApiDate
  } = useDateFormatter();

  /**
   * Convert API refund data to typed format
   * @param {Object} refund - Raw refund from API
   * @returns {Object} Refund with Date objects
   *
   * NOTE: expense_date uses fromApiDateToDate (noon local) because it's a date-only field.
   * Date-only fields should NOT use UTC conversion - they represent calendar dates,
   * not moments in time. "2025-12-18" means December 18th regardless of timezone.
   */
  const convertRefundFromApi = (refund: any) => {
    if (!refund) return null;
    return {
      ...refund,
      // expense_date is date-only: use fromApiDateToDate (noon local) to avoid timezone issues
      expense_date: refund.expense_date ? fromApiDateToDate(refund.expense_date) : null,
      // expense_date_string: keep original string for form population
      expense_date_string: refund.expense_date || null,
      request_date: refund.request_date ? fromApiDateTimeFormat(refund.request_date) : null,
      accepted_at: refund.accepted_at ? fromApiDateTimeFormat(refund.accepted_at) : null,
      declined_at: refund.declined_at ? fromApiDateTimeFormat(refund.declined_at) : null,
      created_at: refund.created_at ? fromApiDateTimeFormat(refund.created_at) : null,
      updated_at: refund.updated_at ? fromApiDateTimeFormat(refund.updated_at) : null,
      // Convert amount from centavos to reais if present
      amount: refund.amount !== undefined ? parseFloat(refund.amount) / 100 : null
    };
  };

  /**
   * Convert array of refunds from API
   * @param {Array} refunds - Array of raw refunds
   * @returns {Array} Refunds with Date objects
   */
  const convertRefundsFromApi = (refunds: any) => {
    if (!Array.isArray(refunds)) return [];
    return refunds.map(convertRefundFromApi);
  };

  /**
   * List refund requests with optional filters
   *
   * @param {Object} filters - Optional filters
   * @param {number} [filters.status] - Status filter (-1, 0, 1)
   * @param {number} [filters.franchise_id] - Franchise filter
   * @param {number} [filters.period_id] - Payroll period filter
   * @param {string} [filters.pays_to] - User filter (recipient)
   * @returns {Promise<Array>} Array of refund requests
   */
  const listRefunds = async (filters: any = {}) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'listRefunds', 'Fetching refunds', filters);

      const params = new URLSearchParams();
      if (filters.status !== undefined && filters.status !== null) {
        params.append('status', filters.status);
      }
      if (filters.franchise_id) {
        params.append('franchise_id', filters.franchise_id);
      }
      if (filters.period_id) {
        params.append('period_id', filters.period_id);
      }
      if (filters.pays_to) {
        params.append('pays_to', filters.pays_to);
      }
      if (filters.exclude_paid) {
        params.append('exclude_paid', '1');
      }

      const queryString = params.toString();
      const url = `/payroll/refunds${queryString ? '?' + queryString : ''}`;

      const response = await httpService.get(url, {
        responseContract: 'Payroll_RefundsResponse'
      });

      if (response.success) {
        const refunds = convertRefundsFromApi(response.dados || []);
        log('usePayrollRefunds', 'listRefunds', 'Refunds fetched', refunds);
        return refunds;
      } else {
        throw new Error(response.error || 'Falha ao buscar reembolsos');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'listRefunds', 'Error fetching refunds', err);
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get a single refund by ID
   *
   * @param {number} refundId - Refund ID
   * @returns {Promise<Object|null>} Refund object or null
   */
  const getRefund = async (refundId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'getRefund', 'Fetching refund', { refundId });

      const response = await httpService.get(`/payroll/refund/${refundId}`, {
        inputContract: 'Payroll_CreateRefundInput',
        responseContract: 'Payroll_RefundResponse'
      });

      if (response.success) {
        const refund = convertRefundFromApi(response.dados);
        log('usePayrollRefunds', 'getRefund', 'Refund fetched', refund);
        return refund;
      } else {
        throw new Error(response.error || 'Falha ao buscar reembolso');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'getRefund', 'Error fetching refund', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new refund request
   *
   * @param {Object} refundData
   * @param {number} refundData.franchise_id - Franchise ID
   * @param {string} refundData.title - Request title (required)
   * @param {string} [refundData.description] - Description
   * @param {number} refundData.amount - Amount in reais (required)
   * @param {string} refundData.blob_name - Azure blob path (required, from upload response)
   * @param {Date|string} refundData.expense_date - Expense date (required)
   * @param {string} [refundData.pays_to] - User sub who receives the payment (auto-filled for operadores)
   * @returns {Promise<Object|null>} Created refund or null
   */
  const createRefund = async (refundData: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'createRefund', 'Creating refund', refundData);

      // Date-only fields: use fromDateToApiDate (local) - no UTC conversion needed
      const payload: Payroll_CreateRefundInput = {
        ...refundData,
        expense_date: refundData.expense_date instanceof Date
          ? fromDateToApiDate(refundData.expense_date)
          : refundData.expense_date
      };

      validate('Payroll_CreateRefundInput', payload);
      const response = await httpService.post('/payroll/create_refund', payload, {
        responseContract: 'Payroll_CreateRefundResponse'
      });

      if (response.success) {
        const refund = convertRefundFromApi(response.dados);
        log('usePayrollRefunds', 'createRefund', 'Refund created successfully', refund);
        return refund;
      } else {
        throw new Error(response.error || 'Falha ao criar solicitação de reembolso');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'createRefund', 'Error creating refund', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update a pending refund request
   *
   * @param {number} refundId - Refund ID
   * @param {Object} updateData - Fields to update
   * @param {string} [updateData.title] - Title
   * @param {string} [updateData.description] - Description
   * @param {number} [updateData.amount] - Amount in reais
   * @param {string} [updateData.blob_name] - Azure blob path (from upload response)
   * @param {Date|string} [updateData.expense_date] - Expense date
   * @returns {Promise<Object|null>} Updated refund or null
   */
  const updateRefund = async (refundId: number | string, updateData: Omit<Payroll_UpdateRefundInput, 'expense_date'> & { expense_date?: Date | string }) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'updateRefund', 'Updating refund', { refundId, updateData });

      // Date-only fields: use fromDateToApiDate (local) - no UTC conversion needed
      const rawDate: Date | string | undefined = updateData.expense_date;
      const payload: Payroll_UpdateRefundInput = {
        ...updateData,
        expense_date: rawDate instanceof Date ? fromDateToApiDate(rawDate) : rawDate
      };

      validate('Payroll_UpdateRefundInput', payload);
      const response = await httpService.put(`/payroll/update_refund/${refundId}`, payload, {
        responseContract: 'Payroll_UpdateRefundResponse'
      });

      if (response.success) {
        const refund = convertRefundFromApi(response.dados);
        log('usePayrollRefunds', 'updateRefund', 'Refund updated successfully', refund);
        return refund;
      } else {
        throw new Error(response.error || 'Falha ao atualizar solicitação de reembolso');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'updateRefund', 'Error updating refund', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a pending refund request
   *
   * @param {number} refundId - Refund ID
   * @returns {Promise<boolean>} Success status
   */
  const deleteRefund = async (refundId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'deleteRefund', 'Deleting refund', { refundId });

      const response = await httpService.delete(`/payroll/delete_refund/${refundId}`, {
        responseContract: 'Payroll_DeleteRefundResponse'
      });

      if (response.success) {
        log('usePayrollRefunds', 'deleteRefund', 'Refund deleted successfully');
        return true;
      } else {
        throw new Error(response.error || 'Falha ao deletar solicitação de reembolso');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'deleteRefund', 'Error deleting refund', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Accept a refund request (creates despesa)
   *
   * @param {number} refundId - Refund ID
   * @param {number} categoriaId - Expense category ID (required)
   * @param {number} [periodId] - Payroll period ID (optional)
   * @returns {Promise<Object|null>} Result with refund and despesa_id or null
   */
  const acceptRefund = async (refundId: number | string, categoriaId: number, periodId?: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'acceptRefund', 'Accepting refund', { refundId, categoriaId, periodId });

      const payload: Payroll_AcceptRefundInput = {
        categoria_id: categoriaId,
        period_id: periodId
      };

      validate('Payroll_AcceptRefundInput', payload);
      const response = await httpService.post(`/payroll/accept_refund/${refundId}`, payload, {
        responseContract: 'Payroll_AcceptRefundResponse'
      });

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty accept refund response');
        }
        const result = {
          refund: convertRefundFromApi(response.dados.refund),
          despesa_id: response.dados.despesa_id
        };
        log('usePayrollRefunds', 'acceptRefund', 'Refund accepted successfully', result);
        return result;
      } else {
        throw new Error(response.error || 'Falha ao aceitar solicitação de reembolso');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'acceptRefund', 'Error accepting refund', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Decline a refund request
   *
   * @param {number} refundId - Refund ID
   * @param {string} [reason] - Optional decline reason
   * @returns {Promise<Object|null>} Updated refund or null
   */
  const declineRefund = async (refundId: number | string, reason?: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'declineRefund', 'Declining refund', { refundId, reason });

      const payload: Payroll_DeclineRefundInput = { reason };

      validate('Payroll_DeclineRefundInput', payload);
      const response = await httpService.post(`/payroll/decline_refund/${refundId}`, payload, {
        responseContract: 'Payroll_DeclineRefundResponse'
      });

      if (response.success) {
        const refund = convertRefundFromApi(response.dados);
        log('usePayrollRefunds', 'declineRefund', 'Refund declined successfully', refund);
        return refund;
      } else {
        throw new Error(response.error || 'Falha ao recusar solicitação de reembolso');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'declineRefund', 'Error declining refund', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get pending refunds for a payroll period
   * Used by payroll wizard to show refunds that need to be processed
   *
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Object with refunds, summary_by_user, and total_pending
   */
  const getPendingRefundsForPeriod = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'getPendingRefundsForPeriod', 'Fetching pending refunds', { periodId });

      const response = await httpService.get(`/payroll/pending_refunds/${periodId}`, {
        responseContract: 'Payroll_PendingRefundsResponse'
      });

      if (response.success) {
        const data = response.dados || { refunds: [], summary_by_user: [], total_pending: 0 };
        // Convert refunds (amounts already converted by API)
        // NOTE: expense_date uses fromApiDateToDate (noon local) to avoid timezone issues
        data.refunds = data.refunds.map((ref: any) => ({
          ...ref,
          expense_date: ref.expense_date ? fromApiDateToDate(ref.expense_date) : null,
          expense_date_string: ref.expense_date || null,
          request_date: ref.request_date ? fromApiDateTimeFormat(ref.request_date) : null,
          created_at: ref.created_at ? fromApiDateTimeFormat(ref.created_at) : null
        }));
        log('usePayrollRefunds', 'getPendingRefundsForPeriod', 'Pending refunds fetched', data);
        return data;
      } else {
        throw new Error(response.error || 'Falha ao buscar reembolsos pendentes');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'getPendingRefundsForPeriod', 'Error fetching pending refunds', err);
      return { refunds: [], summary_by_user: [], total_pending: 0 };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Process multiple refund decisions at once (batch)
   * Used by payroll wizard to batch accept/decline refunds
   *
   * @param {number} periodId - Payroll period ID
   * @param {Array<Object>} decisions - Array of decisions
   * @param {number} decisions[].refund_id - Refund ID
   * @param {string} decisions[].action - 'accept' or 'decline'
   * @param {number} [decisions[].categoria_id] - Category ID (required for 'accept')
   * @param {string} [decisions[].reason] - Decline reason (optional for 'decline')
   * @returns {Promise<Object>} Result with accepted, declined, and errors arrays
   */
  const processRefundsBatch = async (periodId: any, decisions: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'processRefundsBatch', 'Processing batch', { periodId, decisions });

      const payload: Payroll_ProcessRefundsBatchInput = {
        period_id: periodId,
        decisions
      };

      validate('Payroll_ProcessRefundsBatchInput', payload);
      const response = await httpService.post('/payroll/process_refunds_batch', payload, {
        responseContract: 'Payroll_ProcessRefundsBatchResponse'
      });

      if (response.success) {
        log('usePayrollRefunds', 'processRefundsBatch', 'Batch processed successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Falha ao processar reembolsos em lote');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'processRefundsBatch', 'Error processing batch', err);
      return { accepted: [], declined: [], errors: [] };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get refunds summary by user for a period
   * Used for confirmation step to show totals with user details
   *
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Object with summary_by_user, total_refunds, and count
   */
  const getRefundsSummary = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollRefunds', 'getRefundsSummary', 'Fetching summary', { periodId });

      const response = await httpService.get(`/payroll/refunds_summary/${periodId}`, {
        responseContract: 'Payroll_RefundsSummaryResponse'
      });

      if (response.success) {
        log('usePayrollRefunds', 'getRefundsSummary', 'Summary fetched', response.dados);
        return response.dados || { summary_by_user: [], total_refunds: 0, count: 0 };
      } else {
        throw new Error(response.error || 'Falha ao buscar resumo de reembolsos');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollRefunds', 'getRefundsSummary', 'Error fetching summary', err);
      return { summary_by_user: [], total_refunds: 0, count: 0 };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Upload a receipt file for a refund request
   *
   * @param refundId - Refund ID (for logging/tracking)
   * @param file - File to upload
   * @returns Upload result with blob_name and file_url
   */
  const uploadReceipt = async (refundId: number | string, file: File) => {
    log('usePayrollRefunds', 'uploadReceipt', 'Uploading receipt', { refundId, fileName: file.name });
    isLoading.value = true;
    error.value = null;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await httpService.post('/index.php/payroll/upload_refund_receipt', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseContract: 'Payroll_UploadRefundReceiptResponse',
      }) as { success: boolean; dados?: { blob_name?: string; file_url?: string; url?: string }; error?: string };

      if (response.success) {
        log('usePayrollRefunds', 'uploadReceipt', 'Upload successful', response.dados);
        return { blob_name: response.dados?.blob_name, file_url: response.dados?.file_url || response.dados?.url };
      } else {
        throw new Error(response.error || 'Falha ao fazer upload do arquivo');
      }
    } catch (err) {
      log('usePayrollRefunds', 'uploadReceipt', 'Error uploading receipt', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get status label for display
   *
   * @param {number} status - Status code (-1, 0, 1)
   * @returns {string} Status label
   */
  const getStatusLabel = (status: any) => {
    switch (parseInt(status)) {
      case -1:
        return 'Recusado';
      case 0:
        return 'Pendente';
      case 1:
        return 'Aceito';
      default:
        return 'Desconhecido';
    }
  };

  /**
   * Get status severity for PrimeVue badges
   *
   * @param {number} status - Status code (-1, 0, 1)
   * @returns {string} Severity value
   */
  const getStatusSeverity = (status: any) => {
    switch (parseInt(status)) {
      case -1:
        return 'danger';
      case 0:
        return 'warning';
      case 1:
        return 'success';
      default:
        return 'secondary';
    }
  };

  return {
    // State
    isLoading,
    error,

    // CRUD Methods
    listRefunds,
    getRefund,
    createRefund,
    updateRefund,
    deleteRefund,

    // Workflow Methods
    acceptRefund,
    declineRefund,
    getPendingRefundsForPeriod,
    processRefundsBatch,
    getRefundsSummary,
    uploadReceipt,

    // Utility Methods
    getStatusLabel,
    getStatusSeverity
  };
}
